import { Component, ElementRef, HostListener, OnInit, OnDestroy, Renderer2, ViewChild, AfterViewInit } from '@angular/core';

import { NgRedux, select } from '@angular-redux/store';
import { Subscription } from 'rxjs';

import { AppState } from '../../../../interfaces/store.interface';

import { UiActionsService } from '../../../store/actions/ui.actions';

import { CONFIG } from '../../../shared/config/config.const';

@Component({
    selector: 'app-listing-gallery-carousel',
    templateUrl: './listing-gallery-carousel.component.html',
    styleUrls: ['./listing-gallery-carousel.component.scss']
})
export class ListingGalleryCarouselComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild('holder', { static: false }) holder: ElementRef;
    @ViewChild('list', { static: false }) list: ElementRef;
    @ViewChild('mask', { static: false }) mask: ElementRef;

    images: string[];
    buffer: string[];

    index: number; // this represents the index of the data in the images array

    private bufferSize = 3; // one before, one after
    private bufferOffset = Math.floor(this.bufferSize / 2);
    private bufferIndex = this.bufferOffset; // this represents the index of the slide in the dom

    private containerWidth: number;

    private animationFrame: number;
    private animating: boolean;

    private ready: boolean;
    private locked: boolean;
    private pressed: boolean;

    private currentX: number;

    // in container % how much we need to drag to trigger the slide change
    private moveThreshold = 0.05;

    private touchX: number;
    private touchY: number;
    private startX: number;

    private tempIndex: number;

    private imagesSubscription: Subscription;
    private indexSubscription: Subscription;

    private touchstartListener: Function;
    private touchmoveListener: Function;
    private touchendListener: Function;
    private touchcancelListener: Function;

    constructor(
        private ngRedux: NgRedux<AppState>,
        private uiActions: UiActionsService,
        private el: ElementRef,
        private renderer: Renderer2
    ) { }

    ngOnInit() {

        this.images = [];
        this.buffer = [];
        this.index = 0;
        this.currentX = 0;

        // measure the container
        this.getContainerWidth();

        // data arriving
        this.imagesSubscription = this.ngRedux.select<string[]>(['ui', 'gallery', 'images'])
            .subscribe((images: string[]) => {
                this.images = images || [];
                this.ready = !!this.images.length;
            });

        // change to index without animating
        this.indexSubscription = this.ngRedux.select<number>(['ui', 'gallery', 'index'])
            .subscribe((index: number) => {
                this.index = index;
                // @TODO - this value should drive the animation, not the other way around
                // update the buffer
                this.updateBuffer();
            });

        // bind touch event listeners
        this.touchstartListener = this.renderer.listen(this.el.nativeElement, 'touchstart', this.swipeStart.bind(this));
        this.touchmoveListener = this.renderer.listen(this.el.nativeElement, 'touchmove', this.swipeMove.bind(this));
        this.touchendListener = this.renderer.listen(this.el.nativeElement, 'touchend', this.swipeEnd.bind(this));
        this.touchcancelListener = this.renderer.listen(this.el.nativeElement, 'touchcancel', this.swipeEnd.bind(this));

    }

    ngAfterViewInit(): void {
        this.images = [];
        this.buffer = [];
        this.index = 0;
        this.currentX = 0;

        // measure the container
        this.getContainerWidth();

        // data arriving
        this.imagesSubscription = this.ngRedux.select<string[]>(['ui', 'gallery', 'images'])
            .subscribe((images: string[]) => {
                this.images = images || [];
                this.ready = !!this.images.length;
            });

        // change to index without animating
        this.indexSubscription = this.ngRedux.select<number>(['ui', 'gallery', 'index'])
            .subscribe((index: number) => {
                this.index = index;
                // @TODO - this value should drive the animation, not the other way around
                // update the buffer
                this.updateBuffer();
            });

        // bind touch event listeners
        this.touchstartListener = this.renderer.listen(this.el.nativeElement, 'touchstart', this.swipeStart.bind(this));
        this.touchmoveListener = this.renderer.listen(this.el.nativeElement, 'touchmove', this.swipeMove.bind(this));
        this.touchendListener = this.renderer.listen(this.el.nativeElement, 'touchend', this.swipeEnd.bind(this));
        this.touchcancelListener = this.renderer.listen(this.el.nativeElement, 'touchcancel', this.swipeEnd.bind(this));
    }

    ngOnDestroy() {
        this.imagesSubscription.unsubscribe();
        this.indexSubscription.unsubscribe();

        this.touchstartListener();
        this.touchmoveListener();
        this.touchendListener();
        this.touchcancelListener();
    }

    next() {
        this.changeSlide(1);
    }

    previous() {
        this.changeSlide(-1);
    }

    goToSlide(index: number) {
        this.uiActions.setGalleryIndex({ index });
    }

    @HostListener('window:resize', [])
    onResize() {
        this.getContainerWidth();
        this.repositionSlide();
    }

    @HostListener('document:keyup', ['$event'])
    onKeyup(event: KeyboardEvent) {
        if (this.ready && !this.ngRedux.getState().ui.activeModal) {
            if (event.keyCode === 37) {
                this.previous();
            } else if (event.keyCode === 39) {
                this.next();
            } else if (event.keyCode === 27) {
                this.uiActions.closeGallery();
            }
            return;
        }
    }

    private changeSlide(direction) {

        if (!this.locked) {
            // hide the mask
            this.renderer.removeClass(this.mask.nativeElement, 'inline-block');

            // change image directly (outside of Angular - it needs to happen immediately)
            this.renderer.setProperty(this.mask.nativeElement, 'src', this.buffer[this.bufferIndex + direction]);

            this.animateToSlide(this.bufferIndex + direction, direction);
        }
    }

    private updateBuffer() {
        const l = this.images.length;
        const result = [];

        let i = this.index - this.bufferOffset;

        while (result.length < this.bufferSize) {
            let item;

            // reset to start
            if (i >= l - this.bufferOffset) {
                i = -this.bufferOffset;
            }

            if (i < 0) {
                item = this.images[l + i];
            } else {
                item = this.images[i];
            }

            // push to array
            result.push(item);
            // increment i
            i++;
        }

        this.buffer = result;
        this.bufferIndex = this.bufferOffset;

        // get the position right now
        const x = this.calculateSlideX(this.bufferIndex);

        this.move(x);

    }

    private getContainerWidth() {
        const dims = this.el.nativeElement.getBoundingClientRect();
        this.containerWidth = dims.width;
    }

    private calculateSlideX(index: number) {
        return (index * this.containerWidth) * -1;
    }

    private repositionSlide() {
        const x = this.calculateSlideX(this.bufferIndex);
        this.move(x);
    }

    private scroll(offset: number) {
        const x = this.calculateSlideX(this.bufferIndex) + offset;
        this.move(x);
    }

    private move(x: number) {
        if (this.list) {
            this.renderer.setStyle(this.list.nativeElement, 'transform', 'translate3d(' + x + 'px, 0, 0)');
            this.currentX = x;
        }
    }

    private animate(start: number, end: number, callback: Function) {

        const duration = CONFIG.TRANSITION.GALLERY;

        let now = new Date();
        const begin = now.getTime();

        let iteration = 0;

        const change = end - start;

        function transition() {

            const x = this.easeInOutQuad(iteration, start, change, duration);

            this.move(x);

            if (iteration < duration) {
                now = new Date();
                iteration = now.getTime() - begin;
                this.animationFrame = window.requestAnimationFrame(transition.bind(this));
            } else {
                callback();
            }

        }

        // kick off
        transition.call(this);

    }

    private animateToSlide(index: number, direction: number) {

        const x = this.calculateSlideX(index);

        // cancel any pending animation
        window.cancelAnimationFrame(this.animationFrame);

        this.animating = true;

        this.renderer.addClass(this.holder.nativeElement, 'animating');

        this.animate(this.currentX, x, () => {

            // make sure position is accurate
            this.move(x);

            // update index
            this.bufferIndex = index;

            // update main index
            this.index += direction;

            if (this.index >= this.images.length) {
                this.index = 0;
            } else if (this.index < 0) {
                this.index = this.images.length - 1;
            }

            // reset tempIndex
            this.tempIndex = -1;

            if (!this.pressed) {

                this.renderer.removeClass(this.mask.nativeElement, 'inline-block');

                this.updateBuffer();

            }

            this.animating = false;

            this.renderer.removeClass(this.holder.nativeElement, 'animating');

            // ensure the index is up to date
            this.uiActions.setGalleryIndex({ index: this.index });

        });
    }

    private getAbsMoveTreshold() {
        // return min pixels required to move a slide
        return this.moveThreshold * this.containerWidth;
    }


    private swipeCoords(event: TouchEvent) {

        let x, y;

        if (event && event.touches && event.touches.length) {
            x = event.touches[0].pageX;
            y = event.touches[0].pageY;
        }

        return {
            x: x,
            y: y
        };

    }

    private swipeStart(event: TouchEvent) {

        event.preventDefault();
        event.stopPropagation();

        const coords = this.swipeCoords(event);

        // store touch positions
        this.touchX = coords.x;
        this.touchY = coords.y;

        if (!this.animating) {
            this.pressed = true;
            this.startX = coords.x;
            this.locked = false;
        } else {
            this.locked = true;
        }

        // hide the mask
        this.renderer.removeClass(this.mask.nativeElement, 'inline-block');

        // make the mask transparent
        this.renderer.addClass(this.holder.nativeElement, 'swiping');

        return false;
    }

    private swipeMove(event: TouchEvent) {

        event.preventDefault();
        event.stopPropagation();

        const coords = this.swipeCoords(event);

        if (!this.locked) {

            let x, delta;

            if (this.pressed) {

                // store touch positions
                this.touchX = coords.x;
                this.touchY = coords.y;

                x = coords.x;
                delta = x - this.startX;

                if (delta > 2 || delta < -2) {

                    const absMove = (coords.x - this.startX);
                    const minMove = this.getAbsMoveTreshold();
                    const shouldMove = Math.abs(absMove) > minMove;
                    const direction = Math[absMove >= 0 ? 'ceil' : 'floor'](absMove / this.containerWidth) * -1;

                    let newTempIndex = this.bufferIndex;

                    if (shouldMove) {
                        newTempIndex += direction;
                    }

                    if (this.tempIndex !== newTempIndex) {
                        // update temp
                        this.tempIndex = newTempIndex;
                        // change image directly (outside of Angular - it needs to happen immediately)
                        this.renderer.setProperty(this.mask.nativeElement, 'src', this.buffer[this.tempIndex])
                    }

                    window.requestAnimationFrame(() => {
                        this.scroll(delta);
                    });
                }
            }
        }

        return false;
    }

    private swipeEnd(event: TouchEvent) {

        // use touch for last touch position
        const coords = {
            x: this.touchX,
            y: this.touchY
        };

        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        this.renderer.removeClass(this.holder.nativeElement, 'swiping');

        if (!this.locked) {

            this.pressed = false;

            const absMove = (coords.x - this.startX);
            const minMove = this.getAbsMoveTreshold();
            const shouldMove = Math.abs(absMove) > minMove;
            const direction = Math[absMove >= 0 ? 'ceil' : 'floor'](absMove / this.containerWidth) * -1;

            if (shouldMove) {
                // change image directly (outside of Angular - it needs to happen immediately)
                this.renderer.setProperty(this.mask.nativeElement, 'src', this.buffer[this.bufferIndex + direction])
                // move in direction
                this.animateToSlide(this.bufferIndex + direction, direction);
            } else {
                // change image directly (outside of Angular - it needs to happen immediately)
                this.renderer.setProperty(this.mask.nativeElement, 'src', this.buffer[this.bufferIndex])
                // reset current to center
                this.animateToSlide(this.bufferIndex, direction);
            }
        }

        return false;
    }



    // t: current time, b: begInnIng value, c: change In value, d: duration
    private easeInOutQuad(t, b, c, d) {
        if ((t /= d / 2) < 1) {
            return c / 2 * t * t + b;
        }
        return -c / 2 * ((--t) * (t - 2) - 1) + b;
    }

}
